import React, { useState, useRef, useEffect, useCallback } from 'react';
import { auth } from '../firebase';

const ChatbotQuery = ({ onSubmit, initialQuery, resetKey }) => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(() => {
    const base = [
      { sender: "bot", text: "Hi! Who are you looking for?" }
    ];
    if (initialQuery) {
      base.push({ sender: "user", text: initialQuery });
    }
    return base;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNextButton, setShowNextButton] = useState(false);
  const [extractedQuery, setExtractedQuery] = useState("");
  const messagesEndRef = useRef(null);

  // Guard to prevent double API call on mount/reset
  const hasRequestedRef = useRef(false);
  const conversationEndedRef = useRef(false);

  // Check if the conversation has ended (bot message contains //END//)
  const hasConversationEnded = useCallback(() => {
    return messages.some(msg => 
      msg.sender === "bot" && msg.text.includes("//END//")
    );
  }, [messages]);

  // Helper function to clean message text for display
  const cleanMessageForDisplay = (text) => {
    return text
      .replace("//END//", "")
      .replace(/@@[^@]+?@@/g, "")
      .trim();
  };

  // Helper function to extract search query from bot message
  const extractSearchQuery = (text) => {
    if (text.includes("//END//")) {
      const queryMatch = text.match(/@@([^@]+?)@@/);
      if (queryMatch) {
        return queryMatch[1].trim();
      }
    }
    return "";
  };

  // Reset state on resetKey or initialQuery change
  useEffect(() => {
    setMessages(() => {
      const base = [
        { sender: "bot", text: "Hi! Who are you looking for?" }
      ];
      if (initialQuery) {
        base.push({ sender: "user", text: initialQuery });
      }
      return base;
    });
    setError(null);
    setInput("");
    setIsLoading(false);
    setShowNextButton(false);
    setExtractedQuery("");
    hasRequestedRef.current = false; // Reset the guard on reset
    conversationEndedRef.current = false; // Reset the conversation ended flag on reset
  }, [resetKey, initialQuery]);

  // Watch for the latest user message and trigger API call
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    const conversationEnded = hasConversationEnded();
    
    // Only send if:
    // 1. Last message is from user
    // 2. Not already loading
    // 3. Not already requested
    // 4. Conversation hasn't ended
    if (
      lastMsg &&
      lastMsg.sender === "user" &&
      !isLoading &&
      !hasRequestedRef.current &&
      !conversationEnded &&
      !conversationEndedRef.current
    ) {
      hasRequestedRef.current = true; // Set the guard
      setIsLoading(true);
      sendChatToBackend(messages)
        .then(botReply => {
          setMessages(msgs => [...msgs, { sender: "bot", text: botReply }]);
          
          // Check if this reply ends the conversation
          if (botReply.includes("//END//")) {
            conversationEndedRef.current = true;
          }
        })
        .catch(err => {
          setMessages(msgs => [...msgs, { sender: "bot", text: "Sorry, there was an error." }]);
          setError(err.message);
        })
        .finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line
  }, [messages, isLoading, hasConversationEnded]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Handle showing Next button when conversation ends
  useEffect(() => {
    if (hasConversationEnded() && !isLoading) {
      // Extract the search query from the last bot message
      const lastBotMessage = messages
        .filter(msg => msg.sender === "bot")
        .pop();
      
      if (lastBotMessage) {
        const query = extractSearchQuery(lastBotMessage.text);
        setExtractedQuery(query);
      }
      
      // Add a small delay to ensure the final message is displayed
      const timer = setTimeout(() => {
        setShowNextButton(true);
      }, 1000); // 1 second delay
      
      return () => clearTimeout(timer);
    } else if (!hasConversationEnded()) {
      setShowNextButton(false);
      setExtractedQuery("");
    }
  }, [hasConversationEnded, isLoading, messages]);

  async function sendChatToBackend(messages) {
    const formattedMessages = messages.map(msg => ({
      role: msg.sender === "user" ? "user" : "assistant",
      content: msg.text,
    }));
    // Get Firebase ID token
    const user = auth.currentUser;
    if (!user) throw new Error("User not authenticated");
    const token = await user.getIdToken();
    // Use environment variable for base URL
    const baseUrl = process.env.REACT_APP_REFINE_QUERY_URL || "http://localhost:8080";
    const res = await fetch(`${baseUrl}/api/v1/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ messages: formattedMessages }),
    });
    if (!res.ok) throw new Error("Chat API error");
    const data = await res.json();
    return data.reply;
  }

  const handleSend = (e) => {
    e.preventDefault();
    setError(null);
    if (!input.trim()) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(msgs => [...msgs, { sender: "user", text: userMsg }]);
    hasRequestedRef.current = false; // Allow API call for new user message
  };

  const handleNext = () => {
    // Call onSubmit with the conversation data
    onSubmit({
      messages: messages,
      finalQuery: extractedQuery
    });
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[300px]">
      <div className="w-full max-w-xl mx-auto bg-white/60 backdrop-blur-md border border-gray-200/50 rounded-2xl p-6 shadow-lg mb-8 flex flex-col" style={{ height: 420 }}>
        <div className="mb-4 space-y-2 overflow-y-auto flex-1" style={{ minHeight: 0 }}>
          {(() => {
            // Find the index of the last bot message with //END//
            const lastEndIdx = [...messages].reverse().findIndex(
              msg => msg.sender === "bot" && msg.text.includes("//END//")
            );
            // If found, calculate its index from the start
            const hideIdx = lastEndIdx !== -1 ? messages.length - 1 - lastEndIdx : -1;
            // Filter out that message when rendering
            return messages
              .filter((msg, idx) => idx !== hideIdx)
              .map((msg, idx) => (
                <div key={idx} className={`flex ${msg.sender === "bot" ? "justify-start" : "justify-end"}`}>
                  <div className={`px-4 py-2 rounded-lg text-sm ${msg.sender === "bot" ? "bg-gray-100 text-gray-800" : "bg-black text-white"}`}>
                    {msg.sender === "bot" ? cleanMessageForDisplay(msg.text) : msg.text}
                  </div>
                </div>
              ));
          })()}
          {isLoading && (
            <div className="flex justify-start">
              <div className="px-4 py-2 rounded-lg text-sm bg-gray-100 text-gray-800 animate-pulse">Thinking...</div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Show input field and send button only if conversation hasn't ended */}
        {!hasConversationEnded() && (
          <form onSubmit={handleSend} className="flex gap-2 mt-2 items-center">
            <input
              type="text"
              className="flex-1 px-4 py-2 border border-gray-200/50 rounded-lg bg-white/50 backdrop-blur-sm text-sm focus:ring-2 focus:ring-black focus:border-black transition-all duration-200"
              placeholder="Type your message..."
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
            <button
              type="submit"
              className="px-5 py-2 rounded-full bg-black text-white font-semibold shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              disabled={isLoading || !input.trim()}
            >Send</button>
          </form>
        )}
        
        {/* Show extracted query and Next button when conversation has ended */}
        {showNextButton && (
          <div className="mt-4 space-y-6 animate-fade-in">
            {extractedQuery && (
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-5 shadow-sm">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 text-2xl">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                  </svg>
                </span>
                <div>
                  <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">
                    Ready to search for:
                  </div>
                  <div className="text-lg font-bold text-blue-900 break-words">
                    “{extractedQuery}”
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-center">
              <button
                onClick={handleNext}
                className="px-8 py-3 rounded-full bg-black text-white font-semibold shadow-lg transition-all duration-200 hover:bg-gray-800 text-base focus:outline-none focus:ring-2 focus:ring-black"
                aria-label="Proceed to next step"
              >
                Next
              </button>
            </div>
          </div>
        )}
        
        {error && <div className="text-red-500 text-xs mt-2">{error}</div>}
      </div>
    </div>
  );
};

export default ChatbotQuery; 